import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { parseStoryboard, cueAt, prefetchWindow } from './storyVideoModel';

/**
 * The scrub bar's preview strip (handoff §4b): loads the clip's WebVTT storyboard once the
 * viewer opens, hands back `frameAt(seconds)` → `{ url, xywh }`, and warms the handful of
 * frames around the knob while a drag is on so the preview never goes blank. `url` is the
 * API-relative path the message carries (`videoStoryboardUrl` / `storyboardUrl`); the request
 * rides the signed-in axios client. Any future player (a Portal clip, a club video) can use it.
 */
export default function useStoryboard(url) {
  const [cues, setCues] = useState([]);
  const warmed = useRef(new Set());

  useEffect(() => {
    setCues([]);
    warmed.current = new Set();
    if (!url) return undefined;
    let alive = true;
    api.get(url, { responseType: 'text', transformResponse: [(d) => d] })
      .then((r) => { if (alive) setCues(parseStoryboard(r?.data)); })
      .catch(() => { if (alive) setCues([]); });
    return () => { alive = false; };
  }, [url]);

  const frameAt = useCallback((sec) => cueAt(cues, sec), [cues]);

  const prefetch = useCallback((sec) => {
    prefetchWindow(cues, sec).forEach((i) => {
      const u = cues[i]?.url;
      if (!u || warmed.current.has(u)) return;
      warmed.current.add(u);
      const img = new Image();
      img.decoding = 'async';
      img.src = u;
    });
  }, [cues]);

  return { cues, ready: cues.length > 0, frameAt, prefetch };
}
